import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { finesService, errorMessage } from '../../../services/finesService';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { money, KIND, terminal, htmlTable, copyHtml, Tile, TerminalBadge, Notice } from './collectionsShared';
import Button from '../../../ui/Button';
import Badge from '../../../ui/Badge';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import EmptyState from '../../../ui/EmptyState';
import { Field, Input } from '../../../ui/Field/Field';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table/Table';
import { CellText, CellId, CellNumber, CellMuted, toneClass } from '../../../ui/DataTable/cells';
import LoadingSpinner from '../../../components/LoadingSpinner';
import Popover from '../../../ui/Popover';
import { owedParts } from './refunds.logic';

// Deposit refunds (the fourth mode of the Reminder page, permission `ops_refunds`):
// CLOSED airport contracts whose deposit was a CC PURCHASE or CASH, 30+ days after the deposit
// comment, and which Ejar still holds (receipts - refunds / disbursements - contract charges).
// Built by the engine (/api/collections/refunds, nightly 04:30). The accountant refunds by hand
// on Ejar: once the Refund / Disbursement voucher is there the contract drops off by itself;
// "Refund" marks a refund made outside Ejar - the row then stays, marked "Refunded". An OPEN
// contract is never listed and never gets a Refund button (the refund is due after the close).

function OwedBreakdown({ row }) {
    const navigate = useNavigate();
    const parts = owedParts(row);
    return (
        <Popover label={`Still owed · ${row.contract}`} width={320}
            trigger={({ props }) => (
                <button {...props} type="button" className="hover:underline cursor-pointer font-semibold" title="What the customer still owes">{money(row.owed)}</button>
            )}>
            <div className="p-3 text-sm flex flex-col gap-2">
                <div className="font-semibold text-gray-900">Still owed · <span className="font-mono">{row.contract}</span></div>
                {parts.map((p) => (
                    <div key={p.label} className="flex justify-between gap-3">
                        <span><span className="text-gray-900">{p.label}</span><span className="block text-[11px] text-gray-500">{p.note}</span></span>
                        <span className="font-mono tabular-nums text-red-700 whitespace-nowrap">{money(p.amount)}</span>
                    </div>
                ))}
                {!parts.length && <span className="text-gray-500">Nothing is owed.</span>}
                <div className="flex justify-between gap-3 border-t border-gray-200 pt-1.5 font-semibold">
                    <span>Total</span><span className="font-mono tabular-nums">{money(row.owed)}</span>
                </div>
                <Button size="xs" onClick={() => navigate(`/the-engine?tab=customer&q=${encodeURIComponent(row.contract)}`)}>Every line in The Engine</Button>
            </div>
        </Popover>
    );
}

/** "Refund" -> amount + note -> Confirm: the money went back to the customer outside Ejar. */
function RefundButton({ row, onDone }) {
    const [amount, setAmount] = useState(row.toRefund ?? '');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const save = async (close) => {
        setSaving(true); setErr('');
        try {
            await finesService.collectionsRefundMark(row.contract, Number(amount) || 0, note || '');
            onDone(row, { amount: Number(amount) || 0, note, at: new Date().toISOString() });
            close();
        } catch (e) { setErr(errorMessage(e)); }
        finally { setSaving(false); }
    };
    return (
        <Popover label={`Refund ${row.contract}`} width={300}
            trigger={({ props }) => <Button {...props} size="xs" variant="primary">Refund</Button>}>
            {({ close }) => (
                <div className="p-3 text-sm flex flex-col gap-2">
                    <div className="font-semibold text-gray-900">Refund to the customer · <span className="font-mono">{row.contract}</span></div>
                    {row.owed > 0.05 && <div className="text-xs text-amber-800">Still owed {money(row.owed)} - settle it first, or refund less.</div>}
                    <Field label="Amount (AED)" required>
                        <Input type="number" step="0.01" size="sm" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
                    </Field>
                    <Field label="Note" optional>
                        <Input size="sm" value={note} onChange={(e) => setNote(e.target.value)} placeholder="transfer ref / cash / receipt" />
                    </Field>
                    {err && <div className="text-xs text-red-700">{err}</div>}
                    <div className="flex gap-2 justify-end">
                        <Button size="sm" onClick={close}>Cancel</Button>
                        <Button size="sm" variant="primary" onClick={() => save(close)} disabled={saving || !(Number(amount) > 0)}>{saving ? 'Saving…' : 'Confirm refund'}</Button>
                    </div>
                    <span className="text-[11px] text-gray-500">For a refund made outside Ejar - a disbursement entered on Ejar removes the contract by itself.</span>
                </div>
            )}
        </Popover>
    );
}

export default function RefundsPanel() {
    // confirming a refund is its own permission (permissions phase 3, 4 Oct 2026); the tab stays on ops_refunds
    const { currentUser } = useAuth();          // who confirmed, on the row marked here
    const { hasPermission } = usePermissions();
    const canAct = hasPermission('ops_refunds_mark');
    const [refunds, setRefunds] = useState(null);
    const [marked, setMarked] = useState({});           // contract -> { row, index, mark }: refunded here, kept in place
    const [busy, setBusy] = useState('');
    const [err, setErr] = useState('');
    const [copied, setCopied] = useState('');
    const [filter, setFilter] = useState('all');

    const load = async (fresh = false, quiet = false) => {
        if (!quiet) { setBusy(fresh ? 'Rebuilding the refunds list from Ejar (a few minutes)…' : 'Loading refunds…'); setErr(''); }
        try { setRefunds(await finesService.collectionsRefunds(fresh)); } catch (e) { if (!quiet) setErr(errorMessage(e)); }
        finally { if (!quiet) setBusy(''); }
    };
    useEffect(() => { load(false); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!refunds?.stale) return undefined;
        const t = setInterval(() => load(false, true), 20000);
        return () => clearInterval(t);
    }, [refunds?.stale]);   // eslint-disable-line react-hooks/exhaustive-deps

    // the engine drops a refunded contract from its list: here it stays where it was, marked "Refunded"
    const onRefunded = (row, mark) => {
        const index = (refunds?.rows || []).findIndex((x) => x.contract === row.contract);
        setMarked((m) => ({ ...m, [row.contract]: { row, index, mark: { ...mark, user: currentUser?.email || '' } } }));
        load(false, true);
    };

    const kindOf = (r) => (r.deposit?.kind === 'purchase' ? 'CC purchase' : r.deposit?.kind === 'cash' ? 'Cash' : r.deposit?.kind || '');
    const rs = refunds?.summary || {};
    const all = useMemo(() => {
        const out = (refunds?.rows || []).map((r) => (marked[r.contract] ? { ...r, refunded: marked[r.contract].mark } : r));
        Object.values(marked).sort((a, b) => a.index - b.index).forEach(({ row, index, mark }) => {
            if (!out.some((x) => x.contract === row.contract)) out.splice(Math.max(0, Math.min(index, out.length)), 0, { ...row, refunded: mark });
        });
        return out;
    }, [refunds, marked]);
    const FILTERS = { all: () => true, due: (r) => r.stage === 'due', overdue: (r) => r.stage === 'overdue', owed: (r) => (r.owed || 0) > 0.05, unclear: (r) => r.toRefund == null };
    const list = all.filter(FILTERS[filter] || FILTERS.all);
    const pick = (id) => setFilter(filter === id ? 'all' : id);

    const copy = async () => {
        if (!list.length) { setCopied('No deposits due back.'); return; }
        const cols = [
            { h: '#', get: (r, i) => i + 1 },
            { h: 'Contract', get: (r) => r.contract, bold: true },
            { h: 'Customer', get: (r) => r.customer || '' },
            { h: 'Branch', get: terminal, bold: true },
            { h: 'Deposit held on Ejar', get: (r) => `${kindOf(r)} ${money(r.held ?? r.deposit?.amount)}${r.deposit?.receipt ? ` · #${r.deposit.receipt}` : ''}`, bold: true },
            { h: 'Deposit date', get: (r) => (r.deposit?.at || '').slice(0, 10) },
            { h: 'Due since', get: (r) => `${r.dueOn} (day ${r.day})` },
            { h: 'Paid after close', get: (r) => money(r.ejar?.paidAfterClose) },
            { h: 'Still owed', get: (r) => money(r.owed), red: true },
            { h: 'TO REFUND (AED)', get: (r) => (r.toRefund == null ? 'amount unclear' : money(r.toRefund)), bold: true },
            { h: 'Ejar status', get: (r) => r.status_ejar || '' },
        ];
        const tot = list.reduce((a, r) => a + (r.toRefund || 0), 0);
        const html = htmlTable(cols, list, { label: 'TOTAL', cells: { 1: `${list.length} contracts`, 4: money(list.reduce((a, r) => a + ((r.held ?? r.deposit?.amount) || 0), 0)), 8: money(list.reduce((a, r) => a + (r.owed || 0), 0)), 9: money(tot) } });
        const plain = list.map((r, i) => `${i + 1}. ${r.contract} | ${r.customer || ''} | ${terminal(r)} | ${kindOf(r)} ${money(r.held ?? r.deposit?.amount)} | due ${r.dueOn} | TO REFUND ${r.toRefund == null ? '?' : money(r.toRefund)} AED`).join('\n');
        setCopied(await copyHtml(html, plain, `Refunds table copied (${list.length} contracts) - paste it into the email.`));
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
                <div>
                    <h3 className="text-base font-semibold text-gray-900">Deposits to refund · 30 days</h3>
                    <p className="text-sm text-gray-600">
                        Closed airport contracts with a <b>CC purchase</b> or <b>cash</b> deposit, 30+ days after the deposit, still held on Ejar (receipts − refunds − contract charges). To refund = held − still owed. {refunds?.builtAt ? `Built ${refunds.builtAt}` : ''}
                        {refunds?.stale && <Badge variant="warning" size="sm" className="ml-2">Updating from Ejar…</Badge>}
                    </p>
                </div>
                <div className="ds-toolbar">
                    <Button variant="primary" size="sm" onClick={() => load(true)} disabled={!!busy} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Rebuild from Ejar</Button>
                    <Button size="sm" onClick={copy} disabled={!!busy || !list.length} leadingIcon={<Icon glyph={ICONS.mail} size="sm" />}>Copy table for email</Button>
                </div>
            </div>
            {copied && <Notice tone="success">{copied}</Notice>}
            {busy && <div className="flex items-center gap-2 text-sm text-gray-700" role="status"><LoadingSpinner size="sm" />{busy}</div>}
            {err && <Notice tone="danger">{err}</Notice>}

            {refunds && (
                <div className="flex flex-wrap gap-3 items-center">
                    <Tile id="all" label="Contracts" value={rs.total} active={filter === 'all'} onPick={pick} hint="all" />
                    <Tile id="due" label="Due today" value={rs.dueToday} tone="text-red-700" active={filter === 'due'} onPick={pick} hint="day 30 today" />
                    <Tile id="overdue" label="Overdue" value={rs.overdue} tone="text-gray-700" active={filter === 'overdue'} onPick={pick} hint="past day 30" />
                    <Tile label="Deposits held (AED)" value={money(rs.deposits)} tone="text-gray-900" />
                    <Tile id="owed" label="Still owed (AED)" value={money(rs.stillOwed)} tone="text-amber-700" active={filter === 'owed'} onPick={pick} hint="something must be settled first" />
                    <Tile label="To refund (AED)" value={money(rs.toRefund)} tone="text-green-700" />
                    {rs.amountUnknown > 0 && <Tile id="unclear" label="Amount unclear" value={rs.amountUnknown} tone="text-red-700" active={filter === 'unclear'} onPick={pick} hint="Ejar does not show the held amount" />}
                    {filter !== 'all' && (
                        <span className="text-sm text-gray-700">
                            Showing {list.length} of {all.length} ·{' '}
                            <Button variant="link" size="sm" onClick={() => setFilter('all')}>show all</Button>
                        </span>
                    )}
                </div>
            )}

            <TableContainer maxHeight="72vh" className="overflow-auto">
                <Table sticky className="w-full">
                    <TableHead>
                        <TableRow>
                            {[['#', 'end'], ['Contract'], ['Customer'], ['Branch'], ['Deposit held'], ['Deposit date'], ['Due since'], ['Paid after close', 'end'], ['Still owed', 'end'], ['To refund', 'end'], ['Ejar'], ['Refund']].map(([h], i) => (
                                <TableHeader key={i} className="whitespace-nowrap">{h}</TableHeader>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {list.map((r, i) => (
                            <TableRow key={r.contract}>
                                <TableCell><CellNumber value={i + 1} /></TableCell>
                                <TableCell className="whitespace-nowrap"><CellId>{r.contract}</CellId><CellMuted>{r.plate}{r.unlimitedSalik ? ' · Unlimited' : ''}</CellMuted></TableCell>
                                <TableCell><CellText>{r.customer}</CellText></TableCell>
                                <TableCell className="whitespace-nowrap"><TerminalBadge row={r} /></TableCell>
                                <TableCell>
                                    <Badge variant={(KIND[r.deposit?.kind] || KIND.unknown)[1]} size="sm">{kindOf(r)} · {money(r.held ?? r.deposit?.amount)}{r.deposit?.receipt ? ` · #${r.deposit.receipt}` : ''}</Badge>
                                    {r.commentAmount && r.held != null && Math.abs(r.commentAmount - r.held) > 0.5 ? <CellMuted>comment says {money(r.commentAmount)}</CellMuted> : null}
                                </TableCell>
                                <TableCell><span className="ds-cell-date">{(r.deposit?.at || '').slice(0, 10)}</span></TableCell>
                                <TableCell className="whitespace-nowrap">
                                    <Badge variant={r.stage === 'due' ? 'danger' : 'neutral'} size="sm">{r.stage === 'due' ? 'Due today' : `Overdue · day ${r.day}`}</Badge>
                                    <CellMuted>since {r.dueOn}</CellMuted>
                                </TableCell>
                                <TableCell className="ds-cell-num">{money(r.ejar?.paidAfterClose)}</TableCell>
                                <TableCell className={`ds-cell-num ${toneClass(r.owed > 0.05 ? 'danger' : undefined)}`}>
                                    {r.owed > 0.05 ? <OwedBreakdown row={r} /> : money(r.owed)}
                                    {r.owed > 0.05 && <CellMuted tone="danger">settle first</CellMuted>}
                                </TableCell>
                                <TableCell className={`ds-cell-num ${toneClass(r.toRefund == null ? 'danger' : 'success')}`}>{r.toRefund == null ? 'amount unclear' : money(r.toRefund)}</TableCell>
                                <TableCell><CellText>{r.status_ejar}</CellText></TableCell>
                                <TableCell className="whitespace-nowrap">
                                    {r.refunded ? (
                                        <>
                                            <Badge variant="success" size="sm">✓ Refunded</Badge>
                                            <CellMuted>{money(r.refunded.amount)}{r.refunded.user ? ` · ${r.refunded.user.split('@')[0]}` : ''}</CellMuted>
                                        </>
                                    ) : r.isOpen ? <CellMuted>open - not due</CellMuted> : canAct && <RefundButton row={r} onDone={onRefunded} />}
                                </TableCell>
                            </TableRow>
                        ))}
                        {refunds && !list.length && (
                            <TableRow>
                                <TableCell colSpan={12}>
                                    <EmptyState icon={ICONS.wallet} title={filter !== 'all' ? 'Nothing matches this tile.' : 'No CC-purchase / cash deposit is due back right now.'} />
                                </TableCell>
                            </TableRow>
                        )}
                        {!refunds && (
                            <TableRow><TableCell colSpan={12}><div className="py-6"><LoadingSpinner message={busy || 'Loading…'} /></div></TableCell></TableRow>
                        )}
                        {list.length > 0 && (
                            <TableRow className="is-total">
                                <TableCell colSpan={7}>TOTAL · {list.length} contract(s){filter !== 'all' ? ' (filtered)' : ''}</TableCell>
                                <TableCell className="ds-cell-num">{money(list.reduce((a, r) => a + (r.ejar?.paidAfterClose || 0), 0))}</TableCell>
                                <TableCell className={`ds-cell-num ${toneClass('danger')}`}>{money(list.reduce((a, r) => a + (r.owed || 0), 0))}</TableCell>
                                <TableCell className={`ds-cell-num ${toneClass('success')}`}>{money(list.reduce((a, r) => a + (r.toRefund || 0), 0))}</TableCell>
                                <TableCell colSpan={2} />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {refunds?.errors?.length > 0 && <div className="text-xs text-red-700">{refunds.errors.length} contract(s) could not be read from Ejar: {refunds.errors.slice(0, 3).join(' · ')}</div>}
            <div className="text-xs text-gray-600">Click a contract number for Ejar / The Engine / Customer profile.</div>
        </div>
    );
}
